<script setup lang="ts">
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import Chart from 'primevue/chart';

interface Props {
  title: string;
  tooltip: string;
  disciplines: string[];
}

const props = withDefaults(defineProps<Props>(), {
  title: null,
  tooltip: '',
  disciplines: () => [],
});

const { title, tooltip, disciplines } = toRefs(props);

const colors = [
  {
    style: 'green-dark',
    hex: '#078888',
  },
  {
    style: 'green',
    hex: '#8BCFCF',
  },
  {
    style: 'blue',
    hex: '#40B3DD',
  },
  {
    style: 'orange',
    hex: '#EDA745',
  },
  {
    style: 'purple',
    hex: '#7E78D2',
  },
  {
    style: 'pink',
    hex: '#D49BC1',
  },
  {
    style: 'yellow',
    hex: '#FFD500',
  },
];

const totals = ref([]);

const totalResults = ref(0);

const chartData = ref({
  datasets: [
    {
      data: [],
      backgroundColor: [],
    },
  ],
});

watch(
  () => disciplines.value,
  () => defineTotals(),
);

onMounted(() => defineTotals());

// only show the chart when at least one discipline actually has a total
const hasTotals = computed(() => disciplines.value.some(({ total }) => total > 0));

const formattedTotalResults = computed(() => formatNumber(totalResults.value));

const defineTotals = () => {
  totalResults.value = 0;

  disciplines.value.forEach(({ total }) => (totalResults.value += total));

  const chart = {
    data: [],
    backgroundColor: [],
  };

  totals.value = disciplines.value.map(({ total, name }, key) => {
    const color = colors[key % colors.length]; // wrap palette so extra disciplines never overflow
    chart.data.push(total);
    chart.backgroundColor.push(color.hex);

    return {
      total,
      percent: formatPercent(total),
      name,
      style: color.style,
    };
  });

  defineChartData(chart);
};

const defineChartData = (chart) => (chartData.value.datasets[0] = chart);

const formatNumber = (number: number) => new Intl.NumberFormat().format(Math.round(number));

// guard against divide-by-zero when there are no totals
const formatPercent = (total: number) => `${totalResults.value ? ((total / totalResults.value) * 100).toFixed() : 0}%`;
</script>

<template>
  <div class="p-3 border-round-xl surface-100">
    <div class="flex mb-2 align-items-center">
      <h3 class="text-base text-color-secondary flex-1" v-text="title" />
      <Icon v-tooltip="tooltip" name="info" class="text-gray-400 text-base cursor-pointer" />
    </div>
    <div v-if="hasTotals" class="flex">
      <Chart
        type="doughnut"
        :data="chartData"
        class="attendance flex align-items-center bg-white mr-3 p-2 pt-0 border-circle"
      />
      <div class="flex-1 justify-content-center">
        <div v-for="(item, key) in totals" :key="key" class="flex gap-2 mb-2">
          <Icon name="circle" :class="`text-color-${item.style} text-xs`" />
          <span :class="`text-color-${item.style} text-xs flex-1`" v-text="item.name" />
          <span :class="`text-color-${item.style} font-bold text-sm`" v-text="item.total" />
          <span :class="`text-color-${item.style} text-xs text-right w-2rem`" v-text="item.percent" />
        </div>
        <div class="flex p-2 mt-2 border-top-2 border-white">
          <span class="text-color-grey text-xs flex-1">Total</span>
          <span class="text-color-grey font-bold text-sm" v-text="formattedTotalResults" />
        </div>
      </div>
    </div>
    <p v-else class="py-8 text-center text-color-secondary text-sm">No Discipline Totals Available</p>
  </div>
</template>
